import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Badge, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { TagIcon, PlusIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { companyAPI } from '../../services/api';

interface Coupon {
  id: number;
  code: string;
  coupon_type: 'reusable' | 'one_time';
  discount_type: 'percentage' | 'fixed';
  discount_value: string;
  expires_at: string | null;
  min_order_amount: string;
  is_active: boolean;
  times_used: number;
  created_at: string;
}

const EMPTY_FORM = {
  coupon_type: 'reusable' as 'reusable' | 'one_time',
  code: '',
  discount_type: 'percentage' as 'percentage' | 'fixed',
  discount_value: '',
  expires_at: '',
  min_order_amount: '',
};

function randomCode(len = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function WebstoreCoupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await companyAPI.getCoupons();
      setCoupons(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, code: randomCode() });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Coupon code is required'); return; }
    if (!form.discount_value || isNaN(parseFloat(form.discount_value)) || parseFloat(form.discount_value) <= 0) {
      setError('A valid discount value is required'); return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: any = {
        code: form.code.trim().toUpperCase(),
        coupon_type: form.coupon_type,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        min_order_amount: form.min_order_amount || '0',
      };
      const res = await companyAPI.createCoupon(payload);
      setCoupons(prev => [res.data, ...prev]);
      setShowModal(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || Object.values(e?.response?.data || {}).flat().join(', ') || 'Failed to create coupon');
    }
    setSaving(false);
  };

  const toggleActive = async (c: Coupon) => {
    try {
      const res = await companyAPI.updateCoupon(c.id, { is_active: !c.is_active });
      setCoupons(prev => prev.map(x => x.id === c.id ? res.data : x));
    } catch {}
  };

  const handleDelete = async (id: number) => {
    try {
      await companyAPI.deleteCoupon(id);
      setCoupons(prev => prev.filter(c => c.id !== id));
      setDeleteConfirm(null);
    } catch {}
  };

  const activeCoupons = coupons.filter(c => c.is_active);
  const inactiveCoupons = coupons.filter(c => !c.is_active);

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>
              <TagIcon style={{ width: 22, height: 22, marginRight: 8, color: '#059669', verticalAlign: 'middle' }} />
              Coupon Codes
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              {activeCoupons.length} active coupon{activeCoupons.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={openCreate}
            style={{ background: '#059669', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 20px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <PlusIcon style={{ width: 16, height: 16 }} /> Create coupon
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" style={{ color: '#059669' }} /></div>
        ) : coupons.length === 0 ? (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body style={{ textAlign: 'center', padding: '60px 24px' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🏷️</div>
              <div style={{ fontWeight: 700, color: '#374151', marginBottom: 6 }}>No coupon codes yet</div>
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 20 }}>Create your first discount coupon to attract customers.</p>
              <button onClick={openCreate}
                style={{ background: '#059669', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>
                Create your first coupon
              </button>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Code', 'Type', 'Discount', 'Expires', 'Min Order', 'Used', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: h === '' ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...activeCoupons, ...inactiveCoupons].map(c => {
                  const expired = c.expires_at && new Date(c.expires_at) < new Date();
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f8fafc', opacity: c.is_active ? 1 : 0.55 }}>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.95rem', color: '#1e293b', background: '#f0fdf4', padding: '3px 10px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                          {c.code}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.82rem', color: '#475569' }}>
                        {c.coupon_type === 'reusable' ? '♾ Reusable' : '1× One-time'}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: '#059669', fontSize: '0.9rem' }}>
                        {c.discount_type === 'percentage' ? `${parseFloat(c.discount_value)}% off` : `$${parseFloat(c.discount_value).toFixed(2)} off`}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.82rem', color: expired ? '#ef4444' : '#475569' }}>
                        {c.expires_at
                          ? new Date(c.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span style={{ color: '#94a3b8' }}>Never</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.82rem', color: '#475569' }}>
                        {parseFloat(c.min_order_amount) > 0 ? `$${parseFloat(c.min_order_amount).toFixed(2)}` : <span style={{ color: '#94a3b8' }}>None</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.82rem', color: '#475569' }}>
                        {c.times_used} time{c.times_used !== 1 ? 's' : ''}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div onClick={() => toggleActive(c)}
                          style={{ width: 40, height: 22, borderRadius: '11px', background: c.is_active ? '#059669' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', display: 'inline-block' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: c.is_active ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        {deleteConfirm === c.id ? (
                          <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button onClick={() => handleDelete(c.id)}
                              style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                            <button onClick={() => setDeleteConfirm(null)}
                              style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer' }}>Cancel</button>
                          </span>
                        ) : (
                          <button onClick={() => setDeleteConfirm(c.id)}
                            style={{ background: '#fef2f2', border: 'none', borderRadius: '8px', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrashIcon style={{ width: 14, height: 14, color: '#ef4444' }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </Container>

      {/* Create Coupon Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} onClick={() => setShowModal(false)} />
          <div style={{ position: 'relative', background: 'white', borderRadius: '20px', padding: '28px 28px 24px', maxWidth: 480, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Title bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #f1f5f9', paddingBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>Create coupon code</div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.4rem', lineHeight: 1 }}>×</button>
            </div>

            {error && <Alert variant="danger" style={{ fontSize: '0.82rem', borderRadius: '10px', marginBottom: 16 }}>{error}</Alert>}

            {/* Coupon type */}
            <div style={{ marginBottom: 20 }}>
              {[
                { val: 'reusable', label: 'Reusable coupon code', info: 'Can be used any number of times' },
                { val: 'one_time', label: 'One-time coupon code', info: 'Valid for a single use only' },
              ].map(opt => (
                <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                  <div style={{ marginTop: 2, width: 18, height: 18, borderRadius: '50%', border: `2px solid ${form.coupon_type === opt.val ? '#1e293b' : '#d1d5db'}`, background: 'white', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setForm(p => ({ ...p, coupon_type: opt.val as 'reusable' | 'one_time' }))}>
                    {form.coupon_type === opt.val && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1e293b' }} />}
                  </div>
                  <div onClick={() => setForm(p => ({ ...p, coupon_type: opt.val as 'reusable' | 'one_time' }))}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{opt.info}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Code field */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151', margin: 0 }}>Coupon Code *</Form.Label>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{form.code.length}/20</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Form.Control
                  value={form.code}
                  maxLength={20}
                  onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                  style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.05em', borderRadius: '10px', fontSize: '0.95rem' }}
                  placeholder="e.g. SUMMER20"
                />
                <button title="Generate random code" onClick={() => setForm(p => ({ ...p, code: randomCode() }))}
                  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', cursor: 'pointer', color: '#64748b', flexShrink: 0 }}>
                  <ArrowPathIcon style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>

            {/* Discount type + value */}
            <div style={{ marginBottom: 16 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151', marginBottom: 6 }}>Discount Type *</Form.Label>
              <div style={{ display: 'flex', background: '#f8fafc', borderRadius: '10px', padding: 4, gap: 4, marginBottom: 10 }}>
                {[{ val: 'percentage', label: 'Percentage' }, { val: 'fixed', label: 'Fixed Amount' }].map(opt => (
                  <button key={opt.val} onClick={() => setForm(p => ({ ...p, discount_type: opt.val as 'percentage' | 'fixed' }))}
                    style={{ flex: 1, padding: '8px 0', borderRadius: '8px', border: 'none', background: form.discount_type === opt.val ? 'white' : 'transparent', color: '#1e293b', fontWeight: form.discount_type === opt.val ? 700 : 400, fontSize: '0.88rem', cursor: 'pointer', boxShadow: form.discount_type === opt.val ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="number" min={0.01} step={form.discount_type === 'percentage' ? 1 : 0.01}
                  max={form.discount_type === 'percentage' ? 100 : undefined}
                  value={form.discount_value}
                  onChange={e => setForm(p => ({ ...p, discount_value: e.target.value }))}
                  placeholder={form.discount_type === 'percentage' ? '10' : '5.00'}
                  style={{ borderRadius: '10px', paddingRight: 36 }}
                />
                <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: 600 }}>
                  {form.discount_type === 'percentage' ? '%' : '$'}
                </span>
              </div>
            </div>

            {/* Expiration */}
            <div style={{ marginBottom: 16 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Expiration Time <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                style={{ borderRadius: '10px' }}
              />
            </div>

            {/* Minimum order */}
            <div style={{ marginBottom: 24 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Minimum order amount <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></Form.Label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: 600 }}>$</span>
                <Form.Control
                  type="number" min={0} step={0.01}
                  value={form.min_order_amount}
                  onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))}
                  placeholder="0.00"
                  style={{ borderRadius: '10px', paddingLeft: 28 }}
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
              <button onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ background: '#1e293b', color: 'white', border: 'none', borderRadius: '12px', padding: '11px 28px', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
}
